/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnInit} from '@angular/core';
import * as _ from 'lodash';

import template from './day-parting-targeting.component.html';
import {ADN_TARGETING_TYPES} from '../targeting-constants';
import {TargetingTemplateHook} from './targeting-template.component';

import {TARGETING_IMPORTS} from '../targeting-imports';
import {TargetingTemplateComponent} from './targeting-template.component';

@Component({
  imports: [...TARGETING_IMPORTS, TargetingTemplateComponent],
  selector: 'adn-day-parting-targeting',
  template
})
export class DayPartingTargetingComponent implements OnInit {
  @Input() model: any[];
  @Input() tabController: any;
  @Input() overridableTargeting: any;
  @Input() targetType: any = ADN_TARGETING_TYPES.dayParting;
  @Input() underModel: any;
  @Input() mode: string;
  @Input() isLockedDown: boolean;
  @Input() mandatory: boolean;

  vm: any[] = [];
  readonly weekDays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
  readonly daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  readonly earlyMorningHours = [0, 1, 2, 3, 4, 5];
  readonly morningHours = [6, 7, 8, 9, 10, 11];
  readonly afternoonHours = [12, 13, 14, 15, 16, 17];
  readonly eveningHours = [18, 19, 20, 21, 22, 23];
  readonly allHours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

  readonly templateHook: TargetingTemplateHook = {
    getTemplateData: () => {
      this.prepareForSave();
      return {
        vm: _.cloneDeep(this.vm),
        model: _.cloneDeep(this.model || []),
        constant: ADN_TARGETING_TYPES.dayParting,
        summary: ''
      };
    },
    copyTemplate: (tpl: any) => this.copyTemplate(tpl)
  };

  ngOnInit(): void {
    this.initFromModel();

    if (!this.tabController) {
      return;
    }

    const prepareForSave = () => this.prepareForSave();
    const copyTemplate = (tpl: any) => this.copyTemplate(tpl);
    this.tabController.register(ADN_TARGETING_TYPES.dayParting.widget, prepareForSave, copyTemplate);
    this.tabController.callBeforeSubmit(prepareForSave);
    this.tabController.registerAllTabs(prepareForSave);
  }

  addDayPart(): void {
    const item: any = {
      daysOfWeek: _.cloneDeep(this.daysOfWeek),
      days: {},
      hours: {},
      hoursOfDay: [],
      timeZoneSetting: 'SYSTEM',
      editing: true,
      hourString: []
    };
    this.updateDays(item);
    this.vm.unshift(item);
    this.hourOperation(item, this.allHours, true);
  }

  deleteEntry(index: number): void {
    _.pullAt(this.vm, index);
  }

  toggleDay(entry: any, day: string, newValue: boolean): void {
    const isSelected = _.indexOf(entry.daysOfWeek, day) > -1;
    let requiresAction = false;
    if (newValue && !isSelected) {
      entry.daysOfWeek.push(day);
      requiresAction = true;
    }
    if (!newValue && isSelected) {
      _.pull(entry.daysOfWeek, day);
      requiresAction = true;
    }
    if (requiresAction) {
      entry.daysOfWeek.sort(this.daysOfWeekSort);
      entry.days[day] = newValue;
    }
  }

  toggleHour(entry: any, hour: number, newValue: boolean): void {
    const isSelected = _.indexOf(entry.hoursOfDay, hour) > -1;
    let requiresAction = false;
    if (newValue && !isSelected) {
      entry.hoursOfDay.push(hour);
      requiresAction = true;
    }
    if (!newValue && isSelected) {
      _.pull(entry.hoursOfDay, hour);
      requiresAction = true;
    }
    if (requiresAction) {
      entry.hoursOfDay.sort((a: number, b: number) => a - b);
      entry.hourString = this.doHourString(entry);
      entry.hours[hour] = newValue;
    }
  }

  dayOperation(entry: any, days: string[], newValue: boolean): void {
    _.forEach(days, (day) => this.toggleDay(entry, day, newValue));
  }

  hourOperation(entry: any, hours: number[], newValue: boolean): void {
    _.forEach(hours, (hour) => this.toggleHour(entry, hour, newValue));
  }

  hourLabel(hour: number): string {
    return (hour > 9 ? String(hour) : '0' + hour) + ':00';
  }

  private daysOfWeekSort = (a: string, b: string) => {
    return _.indexOf(this.daysOfWeek, a) - _.indexOf(this.daysOfWeek, b);
  };

  private doHourString(entryObj: any): any[] {
    const hourString = [];
    let prevHour = -100;
    _.forEach(entryObj.hoursOfDay, (hour: number) => {
      if (hourString.length === 0) {
        hourString.push({start: hour, end: hour, string: ''});
      } else if (prevHour + 1 === hour) {
        hourString[hourString.length - 1].end = hour;
      } else {
        hourString.push({start: hour, end: hour, string: ''});
      }
      prevHour = hour;
    });
    _.forEach(hourString, (hsObj: any) => {
      hsObj.string = hsObj.start + ':00-' + (hsObj.end || hsObj.start) + ':59';
    });
    return hourString;
  }

  private updateDays(vmObj: any): void {
    vmObj.daysOfWeek.sort(this.daysOfWeekSort);
    _.forEach(vmObj.daysOfWeek, (day: string) => {
      vmObj.days[day] = true;
    });
  }

  private initFromModel(): void {
    this.vm = _.cloneDeep(this.model || []);
    _.forEach(this.vm, (entryObj: any) => {
      entryObj.days = {};
      this.updateDays(entryObj);

      const hourArray = [];
      _.forEach(entryObj.hoursOfDay, (hour: any) => {
        hourArray.push(parseInt(hour, 10));
      });
      hourArray.sort((a, b) => a - b);
      entryObj.hoursOfDay = hourArray;

      entryObj.hours = {};
      _.forEach(entryObj.hoursOfDay, (hour: number) => {
        entryObj.hours[hour] = true;
      });
      entryObj.hourString = this.doHourString(entryObj);
    });
  }

  private prepareForSave(): void {
    if (!_.isArray(this.model)) {
      return;
    }
    this.model.length = 0;
    _.forEach(this.vm, (entryObj: any) => {
      if (entryObj.daysOfWeek.length > 0 && entryObj.hoursOfDay.length > 0) {
        this.model.push(_.pick(entryObj, ['daysOfWeek', 'hoursOfDay', 'timeZoneSetting']));
      }
    });
    this.initFromModel();
  }

  private copyTemplate(tpl: any): void {
    if (!_.isArray(this.model)) {
      return;
    }
    const copied = _.cloneDeep(_.get(tpl, ['data', 'model'])) || [];
    this.model.length = 0;
    Array.prototype.push.apply(this.model, copied);
    this.initFromModel();
  }
}
